'use client'

import { useRef } from 'react'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { Button } from './button'

/** Edge length of the PNG handed to the user — print-safe at ~1024px. */
const DOWNLOAD_SIZE = 1024

type QRCodeProps = {
  /** What the QR encodes — typically the event share URL. */
  value: string
  /** Pixel size; defaults to a tile that matches the placeholder. */
  size?: number
  /** Caption shown under the code. Pass null/empty to hide. */
  caption?: string | null
  /**
   * Show a "Download QR Code" button under the caption. Gated by the caller —
   * only an event's facilitators/hosts and admins are offered the export.
   */
  downloadable?: boolean
  /** Basename for the downloaded file, without extension. */
  downloadFileName?: string
}

export function QRCode({
  value,
  size = 128,
  caption = 'Share this QR code so others can view the event details.',
  downloadable = false,
  downloadFileName = 'event',
}: QRCodeProps) {
  // The visible tile stays an SVG (crisp at any zoom). The export renders a
  // second, offscreen canvas at print resolution so `toDataURL` gives us a
  // high-res PNG without rasterising/serialising the on-screen SVG.
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const handleDownload = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = `${downloadFileName}-qr-code.png`
    link.click()
  }

  return (
    <div className="flex flex-col items-center justify-center p-3 bg-white rounded-lg border border-border">
      <QRCodeSVG
        value={value}
        size={size}
        level="M"
        marginSize={2}
        className="mb-2"
      />
      {caption ? (
        <p className="text-xs text-neutral text-center">{caption}</p>
      ) : null}
      {downloadable ? (
        <>
          {/* Offscreen, never shown — the source bitmap for the download.
              bgColor is explicit white so the PNG is opaque (a transparent
              background prints as whatever is underneath and can fail a
              scan). */}
          <QRCodeCanvas
            ref={canvasRef}
            value={value}
            size={DOWNLOAD_SIZE}
            level="M"
            marginSize={2}
            fgColor="#000000"
            bgColor="#FFFFFF"
            aria-hidden
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="mt-2 w-full max-w-[220px]"
          >
            Download QR Code
          </Button>
        </>
      ) : null}
    </div>
  )
}
